import { getGa4EcommerceItems, getGa4EcommerceCartItems } from './helpers';
import { ga4EventEcommerceService } from './ga4EventEcommerce.service';
import { ga4EventService } from './ga4Event.service';
import { CartItemType, FacetChange, Product } from '@frodo/models/types';

export type InsurancePlacement =
  | 'pdp_atc_modal'
  | 'product_preview_atc_modal'
  | 'cart';

export function useGa4Events() {
  const trackAddToCart = (
    product: Product,
    quantity: number = 1,
    context?: { widget?: string; position?: number },
  ) => {
    const currency = window.tc_vars?.product_currency;

    ga4EventEcommerceService.addToCart(
      {
        currency,
        value: parseFloat(product.unitPriceTf ?? '0') * quantity,
        items: getGa4EcommerceItems([product], {
          currency,
          quantity,
        }),
      },
      {
        widget: context?.widget,
        position: context?.position,
      },
    );
  };

  const trackFilterAccordionToggle = (
    filterName: string,
    isExpanded: boolean,
  ) => {
    ga4EventService.fireEvent('con_filter_accordion', {
      filter_name: filterName,
      filter_action: isExpanded ? 'expand' : 'collapse',
    });
  };

  const trackFilterSelect = (
    facetChange: FacetChange,
    listingContext: 'srp' | 'crp',
  ) => {
    const { facet, value, valueFrom, valueTo } = facetChange;
    const isRange = facet.type === 'number' && facet.values.length === 0;

    ga4EventService.fireEvent('con_filter_select', {
      filter_name: facet.displayName || facet.key,
      filter_type: isRange ? 'range' : 'multi_select',
      listing_context: listingContext,
      ...(value !== undefined ? { filter_value: value } : {}),
      ...(valueFrom !== undefined ? { filter_value_from: valueFrom } : {}),
      ...(valueTo !== undefined ? { filter_value_to: valueTo } : {}),
    });
  };

  const trackViewItem = (product: Product) => {
    const currency = window.tc_vars?.product_currency;

    ga4EventEcommerceService.viewItem({
      currency,
      value: parseFloat(product.unitPriceTf ?? '0'),
      items: getGa4EcommerceItems([product], { currency }),
    });
  };

  const trackViewItemList = (
    products: Product[],
    meta: { itemListId?: string; itemListName?: string },
  ) => {
    if (products.length === 0) {
      return;
    }

    const currency = window.tc_vars?.product_currency;

    ga4EventEcommerceService.viewItemList({
      item_list_id: meta.itemListId,
      item_list_name: meta.itemListName,
      items: getGa4EcommerceItems(products, {
        currency,
        itemListId: meta.itemListId,
        itemListName: meta.itemListName,
      }),
    });
  };

  const trackViewCart = (cartItems: CartItemType[], currency: string) => {
    const value = cartItems.reduce(
      (total, item) => total + item.unitNetPrice * item.quantity,
      0,
    );

    ga4EventEcommerceService.viewCart({
      currency,
      value: Number(value.toFixed(2)),
      items: getGa4EcommerceCartItems(cartItems, { currency }),
    });
  };

  const trackViewRecentlyPurchasedWidget = (
    contentType: string,
    shownItemsCount: number,
  ) => {
    ga4EventService.fireEvent('con_recently_purchased_widget_viewed', {
      content_type: contentType,
      items_shown_count: shownItemsCount,
    });
  };

  const trackRecentlyPurchasedToggleClick = (
    toggleType: 'see_more' | 'see_less',
  ) => {
    ga4EventService.fireEvent('con_recently_purchased_toggle_clicked', {
      toggle_type: toggleType,
    });
  };

  const trackViewRelatedSetsWidget = (
    ctaType: 'view_bundle' | 'open_modal',
    sku: string,
  ) => {
    ga4EventService.fireEvent('con_related_sets_widget_viewed', {
      cta_type: ctaType,
      sku: sku,
    });
  };

  const trackRelatedSetsClick = (
    ctaType: 'view_bundle' | 'open_modal',
    sku: string,
  ) => {
    ga4EventService.fireEvent('con_related_sets_clicked', {
      cta_type: ctaType,
      sku: sku,
    });
  };

  const trackRelatedSetsModalOpen = (
    sku: string,
    items: {
      item_id: string;
      index: number;
      price: string;
      discount: number;
    }[],
  ) => {
    ga4EventService.fireEvent('con_related_sets_modal_open', {
      sku: sku,
      items: items,
    });
  };

  const trackInsuranceAdded = (params: {
    sku: string;
    placement: InsurancePlacement;
    offerId: string;
    insurancePrice: number;
  }) => {
    ga4EventService.fireEvent('con_insurance_added', {
      sku: params.sku,
      insurance_placement: params.placement,
      insurance_offer_id: params.offerId,
      insurance_price: params.insurancePrice,
    });
  };

  const trackInsuranceRemoved = (params: {
    sku: string;
    placement: InsurancePlacement;
    offerId: string;
    insurancePrice: number;
  }) => {
    ga4EventService.fireEvent('con_insurance_removed', {
      sku: params.sku,
      insurance_placement: params.placement,
      insurance_offer_id: params.offerId,
      insurance_price: params.insurancePrice,
    });
  };

  const trackInsuranceInfoOpened = (params: {
    sku: string;
    placement: InsurancePlacement;
  }) => {
    ga4EventService.fireEvent('con_insurance_info_opened', {
      sku: params.sku,
      insurance_placement: params.placement,
    });
  };

  const trackInsuranceTermsClicked = (params: {
    sku: string;
    placement: InsurancePlacement;
  }) => {
    ga4EventService.fireEvent('con_insurance_terms_clicked', {
      sku: params.sku,
      insurance_placement: params.placement,
    });
  };

  return {
    trackAddToCart,
    trackViewItem,
    trackViewItemList,
    trackFilterAccordionToggle,
    trackFilterSelect,
    trackViewCart,
    trackViewRecentlyPurchasedWidget,
    trackRecentlyPurchasedToggleClick,
    trackViewRelatedSetsWidget,
    trackRelatedSetsClick,
    trackRelatedSetsModalOpen,
    trackInsuranceAdded,
    trackInsuranceRemoved,
    trackInsuranceInfoOpened,
    trackInsuranceTermsClicked,
  };
}
